import React, { useEffect, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { hideAlert } from '../../redux/alertSlice';
import { RootState } from '../../redux/store';

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref}  {...props} />;
});

const AlertBox: React.FC = () => {
  const dispatch = useDispatch();
  const alert = useSelector((state: RootState) => state.alert);
  const { alertOpen, alertMessage, alertSeverity } = alert;
  // Bug 83974: AlertBox mounts with the signed-in Layout, so an alert already in the store at that
  // point was raised outside this session's UI - still open at logout, from a request that failed
  // after logout, or on the sign-in page - and would otherwise reappear after the next login.
  // A showAlert/hideAlert that changes the state replaces the slice object, so the guard below hides
  // the leftover for the first render, before the effect clears it.
  const [alertAtMount] = useState(alert);
  // Clear that leftover too. Immer returns the SAME object when a reducer writes values the state
  // already holds, so while a stale `alertOpen: true` sat in the store, an identical showAlert in
  // this session (the same request failing again) would equal alertAtMount and never show.
  // Only if the store still holds it: a sibling's mount effect (Header, Sidenav) runs first and may
  // already have raised a real toast. The Layout's PendingToast effect runs after this one (child
  // effects first), so the hand-off is not affected.
  const store = useStore<RootState>();
  useEffect(() => {
    if (alertAtMount.alertOpen && store.getState().alert === alertAtMount) {
      dispatch(hideAlert({ severity: alertAtMount.alertSeverity }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleClose = () => {
    dispatch(hideAlert({ severity: alertSeverity }));
  };
  return (
    <Snackbar
      open={alertOpen && alert !== alertAtMount}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      autoHideDuration={6000}
      onClose={handleClose}
    >
      <Alert sx={{
        whiteSpace: "normal",
        wordBreak: "break-word",
        maxWidth: "600px"
      }} onClose={handleClose} severity={alertSeverity} >
        {alertMessage}
      </Alert>
    </Snackbar>
  );
};

export default AlertBox;